import {useContext, useState, useEffect} from "react";
import {ServicesWithIdSchema} from "../../types";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import useAsyncError from "@hooks/useAsyncError";
import {NotificationsContext} from "@ui-components/Notifications";
import { useIntl } from "react-intl";

export const useServices = () => {

  // context
  const {push} = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const countryApi = useCountryAxiosInstance();
  const throwError = useAsyncError();

  // state
  const [services, setServices] = useState<ServicesWithIdSchema[]>();
  const [loading, setLoading] = useState(false);
  
  // handlers
  const update = async (
    id: number,
    data: ServicesWithIdSchema,
  ) => {
    setLoading(true);
    try {
      const response = await countryApi!.put(`/services/${id}`, data) as ServicesWithIdSchema;
      setServices(
        (prev) => !!prev
          ? prev.map((s) => s.id === id ? response : s)
          : [response]
      );
      push({type: 'success', title: intl.formatMessage({id: 'service_updated_successfully'})});
      return response;
    } catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'}), message: "SERVICE_UPDATE"});
      throwError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await countryApi!.get('/services/all') as ServicesWithIdSchema[];
        setServices(response);
      }
      catch (error) {
        push({type: 'error', title: intl.formatMessage({id: 'server_error'}), message: "GET_SERVICES"});
        throwError(error);
      }
      finally {
        setLoading(false);
      }
    })();
  }, []);

  const reFetchOnlyOne = async (id: number) => {
    setLoading(true);
    try {
      const response = await countryApi!.get(`/services/${id}`) as ServicesWithIdSchema;
      setServices(
        (prev) => !!prev
          ? prev.map((s) => s.id === id ? response : s)
          : [response]
      );
      return response;
    } catch (error) {
      push({type: 'error', title: intl.formatMessage({id: 'server_error'}), message: "SERVICE_FETCH"});
      throwError(error);
    } finally {
      setLoading(false);
    }
  }

  return {services, update, loading, reFetchOnlyOne};
}